export const AppConfig = {
  appName: 'LegacyTies',
  siteName: 'legacy-ties',
  appContactEmail: 'contact@legacy-ties.com',
  calendlyLink: 'https://calendly.com/chigozie10/2hr',
  socials: {
    X: 'https://x.com/legacyties_1',
    facebook: 'https://www.facebook.com/people/LegacyTies/61575078722118/',
    instagram: 'https://www.instagram.com/legacyties_official/',
    tikTok: 'https://www.tiktok.com/@legacyties',
    linkedIn: 'https://www.linkedin.com/company/legacyties/about/',
  },
};

export const envVariables = {
  localEnv: {
    apiURL: 'REACT_APP_API_URL',
  },
  firebaseEnv: {
    apiKey: 'REACT_APP_FIREBASE_API_KEY',
    authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
    projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
    storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
    messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    appId: 'REACT_APP_FIREBASE_APP_ID',
  },
};

export const maxFamilyTrees = 1 as const;
export const maxFamilyMembers = 10 as const;
export const maxMigrationRecords = 2 as const;
export const maxFamilyStories = 2 as const;
export const maxFileSize = 50 * 1024 * 1024; // 50MB
export const freemiumTreeCollaborators = 1 as const;
export const maxMediaFiles = 3 as const;
export const maxCollaborators = 20 as const;

export const pricingPlans = [
  {
    id: 'free',
    title: 'The Seed (Starter Plan)',
    subTitle: 'For those just beginning to discover their roots.',
    price: '0',
    description: [
      `Create up to ${maxFamilyTrees} family tree(s)`,
      `Add up to ${maxFamilyMembers} family member(s)`,
      'Record names, birthdates, and places of origin',
      `Save up to ${maxFamilyStories} family storie(s), including audio recordings`,
      `Map up to ${maxMigrationRecords} family journey(s)`,
      `Invite ${freemiumTreeCollaborators} family collaborator(s)`,
      `Full access to the ${AppConfig.appName} Cultural Hub`,
      'Privacy and data controls',
    ],
    scheduleMeeting: false,
    buttonText: 'Start Building for Free',
    buttonVariant: 'outlined',
    popular: false,
    status: true,
    notes: null,
  },
  {
    id: 'assisted',
    title: 'The Griot (Concierge Launch)',
    subTitle:
      'For busy people who\u2019d rather we build it for them, then continue on their own.',
    price: '29.99',
    description: [
      '2-hour guided setup session with your dedicated Griot',
      'Your family tree built, migrated and ready to go',
      'Family collaboration set up so relatives can join in',
      'Once set up, simply subscribe to Roots to keep building',
      'A head start, done for you \u2014 no forms, no typing',
    ],
    scheduleMeeting: true,
    buttonText: 'Pay for Griot Setup',
    buttonVariant: 'outlined',
    popular: false,
    status: false,
    notes:
      'Choose a time that works for you. Then pay before or during your session.',
  },
  {
    id: 'premium',
    title: 'The Roots (Builder Plan)',
    subTitle: 'For families actively gathering stories, photos, and records.',
    price: '4.99',
    description: [
      'Unlimited family trees',
      'Unlimited family members',
      'Add family photos',
      'Detailed profiles for every family member',
      'Unlimited family stories, including audio recordings',
      'Unlimited family journeys',
      `Invite up to ${maxCollaborators} family collaborators`,
      'Download your family tree as an image',
      'Import and export GEDCOM files',
      `Full access to the ${AppConfig.appName} Cultural Hub`,
      'Privacy and data controls',
      'Priority support',
    ],
    scheduleMeeting: false,
    buttonText: 'Secure Your Family Roots',
    buttonVariant: 'contained',
    popular: true,
    status: false,
    notes: null,
  },
  {
    id: 'community',
    title: 'The Village (Community Plan)',
    subTitle:
      'For communities, schools, corporations and family networks preserving a shared heritage.',
    price: 'Custom',
    description: [
      'Tailored solutions for community and family networks',
      `Full access to the ${AppConfig.appName} Cultural Hub`,
      'Dedicated onboarding and priority support',
    ],
    scheduleMeeting: false,
    buttonText: 'Talk to Us',
    buttonVariant: 'outlined',
    popular: false,
    status: false,
    notes: null,
  },
] as const;

// Pricing FAQ
export const faqs = [
  {
    question:
      "My grandmother has the stories, but she isn't tech-savvy. Does she have to pay?",
    answer:
      'No. We follow the Elders Eat Free principle. If you buy the Roots plan, you can invite your elders as contributors. They simply click a link to view photos; they never see a paywall.',
  },
  {
    question: 'Can I change my plan later?',
    answer:
      'Yes, you can upgrade or downgrade your plan at any time from your account settings.',
  },
  {
    question: 'Can I start for free and upgrade later?',
    answer:
      'Absolutely. Start with The Seed to map out what you know, parents, grandparents, and siblings. When you are ready to upload photos, add stories or invite cousins to collaborate, you can upgrade to a paid plan instantly.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards. For annual plans, invoicing is available upon request.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes, you can cancel your subscription at any time. Your plan will remain active until the end of your billing period.',
  },
  {
    question: 'Is this a safe place for my data?',
    answer:
      'We know that for many in the diaspora, trust is everything. We are not selling your data to advertisers. Your fee pays for enterprise-level security and encryption to ensure your family history is preserved privately and safely.',
  },
] as const;

export const month = 'month' as const;
export const year = 'year' as const;
/**
 * Sanitise User Input
 */
export const textLimits = {
  personName: 80,
  birthPlace: 200,
  biography: 2500,
  treeName: 150,
  treeDescription: 1000,
  storyTitle: 150,
  storyContent: 4000,
  migrationTitle: 150,
  migrationDescription: 4000,
  migrationEvent: 500,
  migrationLocation: 200,
} as const;
